import React, { useState, useEffect } from "react";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartOptions,
} from "chart.js";
import {
  ArrowDownCircle,
  ArrowRight,
  ArrowUpCircle,
  PiggyBank,
  UserCheck,
  Users,
  Wallet,
} from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

type User = {
  id: string;
  name: string;
  email: string;
  number: string;
  total_deposit_usdt: number;
  total_withdrawal_usdt: number;
};

interface DepositTransaction {
  uid: number | string;
  date: string;
  txtid: string;
  amount: number;
  coin: string;
}

interface LiveBet {
  game: string;
  uid: number;
  amount: number;
  coin: string;
  name: string;
}

interface WithdrawalTransaction {
  uid: number | string;
  date: string;
  wallet: string;
  amount: number;
  coin: string;
  name: string | null;
}

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [userCount, setUserCount] = useState(0);
  const [totalDeposits, setTotalDeposits] = useState(0);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [todayDeposits, setTodayDeposits] = useState(0);
  const [todayWithdrawals, setTodayWithdrawals] = useState(0);
  const [liveBets, setLiveBets] = useState<LiveBet[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [transactions, setTransactions] = useState<
    Array<DepositTransaction | WithdrawalTransaction>
  >([]);

  const [chartData1, setChartData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: "indigo",
      },
    ],
  });

  const [pieData, setPieData] = useState({
    labels: ["Crash", "Mines", "Other Games"],
    datasets: [
      {
        data: [0, 0, 0],
        backgroundColor: ["#4338ca", "#818cf8", "#c7d2fe"],
      },
    ],
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        
        const userResponse = await fetch("https://api.trubetx.com/users");
        const userData = await userResponse.json();
        setUsers(userData);
        setUserCount(userData.length);

        const totalDepositsResponse = await fetch(
          "https://api.trubetx.com/total-deposits"
        );
        const totalDepositsData = await totalDepositsResponse.json();
        setTotalDeposits(totalDepositsData.total_deposits_usdt);

        const totalWithdrawalsResponse = await fetch(
          "https://api.trubetx.com/total-withdrawals"
        );
        const totalWithdrawalsData = await totalWithdrawalsResponse.json();
        setTotalWithdrawals(totalWithdrawalsData.total_withdrawals_usdt);
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };

    const fetchTodayDepositsAndWithdrawals = async () => {
      try {
        const today = new Date().toISOString().split("T")[0];

        const depositResponse = await fetch(
          "https://api.trubetx.com/today-deposits"
        );
        const depositData = await depositResponse.json();

        const totalDepositAmountToday = depositData.reduce(
          (total: any, deposit: { date: string; amount: any }) => {
            const depositDate = deposit.date.split("T")[0];
            return depositDate === today ? total + deposit.amount : total;
          },
          0
        );
        setTodayDeposits(totalDepositAmountToday);

        const withdrawResponse = await fetch(
          "https://api.trubetx.com/today-withdrawals"
        );
        const withdrawData = await withdrawResponse.json();

        const totalWithdrawalAmountToday = withdrawData.reduce(
          (total: any, withdraw: { date: string; amount: any }) => {
            const withdrawDate = withdraw.date.split("T")[0];
            return withdrawDate === today ? total + withdraw.amount : total;
          },
          0
        );
        setTodayWithdrawals(totalWithdrawalAmountToday);
      } catch (error) {
        console.error("Failed to fetch deposit or withdrawal data:", error);
      }
    };


    const getDashboardData = async () => {
      try{

        let response = await fetch("https://api.trubetx.com/admin/getDashboardData");

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        if (!data || !data.data) {
          throw new Error("Invalid data format");
        }
        // Assuming data.data contains the dashboard data
        setDashboardData(data?.data || {});

        console.log("Dashboard Data:", data);

      }catch(error) {
        console.error("Failed to fetch dashboard data:", error);
      }
    }

    fetchTodayDepositsAndWithdrawals();
    fetchUserData();
    getDashboardData();
  }, []);

  const fetchTodayDeposits = async () => {
    const response = await fetch("https://api.trubetx.com/today-deposits");
    const data: DepositTransaction[] = await response.json();
    const today = new Date().toISOString().split("T")[0];
    const todayDeposits = data.filter(
      (deposit) => new Date(deposit.date).toISOString().split("T")[0] === today
    );
    setTransactions(todayDeposits);
  };

  const fetchTodayWithdrawals = async () => {
    const response = await fetch(
      "https://api.trubetx.com/today-withdrawals"
    );
    const data: WithdrawalTransaction[] = await response.json();
    const today = new Date().toISOString().split("T")[0];
    const todayWithdrawals = data.filter(
      (withdrawal) =>
        new Date(withdrawal.date).toISOString().split("T")[0] === today
    );
    setTransactions(todayWithdrawals);
  };

  const handleShowTransactions = async (type: "deposit" | "withdrawal") => {
    setShowModal(true);
    if (type === "deposit") {
      await fetchTodayDeposits();
    } else {
      await fetchTodayWithdrawals();
    }
  };

  const fetchLiveBets = async () => {
    try {
      const response = await fetch("https://api.trubetx.com/live-bets");
      const data: LiveBet[] = await response.json();

      // Check if data is an array
      if (Array.isArray(data)) {
        setLiveBets(data);
      } else {
        console.error("Fetched data is not an array");
        setLiveBets([]); // Set empty array if data is not valid
      }
    } catch (error) {
      console.error("Failed to fetch live bets:", error);
      setLiveBets([]); // Set empty array in case of error
    }
  };

  useEffect(() => {
    fetchLiveBets();
    const interval = setInterval(fetchLiveBets, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Update chart data when liveBets changes

    // setChartData({
    //   labels: Object.keys(gameData),
    //   datasets: [{
    //     data: Object.values(gameData),
    //     backgroundColor: 'indigo',
    //   }]
    // });

    // Update pie chart data

    const crashBets = liveBets
      .filter((bet) => bet.game === "crash")
      .reduce((sum, bet) => sum + bet.amount, 0);
    const mineBets = liveBets
      .filter((bet) => bet.game === "mine")
      .reduce((sum, bet) => sum + bet.amount, 0);
    const otherBets = liveBets
      .filter((bet) => bet.game !== "crash" && bet.game !== "mine")
      .reduce((sum, bet) => sum + bet.amount, 0);

    setPieData({
      labels: ["Crash", "Mines", "Other Games"],
      datasets: [
        {
          data: [crashBets, mineBets, otherBets],
          backgroundColor: ["#4338ca", "#818cf8", "#c7d2fe"],
        },
      ],
    });
  }, [liveBets]);

  const gameData = {
    Mine: 400000,
    Crash: 600000,
    Plinko: 1480000,
    High_Low: 700000,
    Hash_Dice: 100000,
    SingleKeno: 20000,
    Hilo: 10000,
  };

  const chartData = {
    labels: Object.keys(gameData),
    datasets: [
      {
        data: Object.values(gameData),
        backgroundColor: "#394a6a", // Indigo color
        barThickness: 40,
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        // text: 'Live Bets by Game',
        font: {
          size: 18,
          weight: "bold" as const,
        },
        padding: {
          top: 10,
          bottom: 30,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "black", // This line changes the x-axis label color to white
        },
        border: {
          color: "black", // This changes the x-axis line color to white
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
        ticks: {
          color: "black", // This line changes the text color to white
          callback: function (value) {
            return (value as number).toLocaleString();
          },
          stepSize: 200000,
          // max: 1600000,
        },
        border: {
          color: "black", // This changes the x-axis line color to white
        },
      },
    },
  };
  return (
    <div className="ml-0 p-3 lg:p-0 lg:ml-[256px] h-screen ">
      <h1 className="text-3xl font-bold mb-8 text-[#292524]">Dashboard</h1>

      <div className="mt- grid h-fit w-full gap-5">
      
      <div className="flex flex-wrap items-center gap-5">
        
        <div className="min-w-[14rem] flex-1">
          <div className=" rounded-lg bg-white  pb-2 pt-5 ">
            <div className="flex px-5">
              <h2 className="mb-2 font-semibold text-cm-gray">Players</h2>
              
            </div>
            <div title={94} className="cursor-pointer  flex items-center px-5">
              <p className="text-2xl font-bold">{dashboardData?.totalUsers}</p>
            </div>
            <div className="mx-2 mt-2 grid grid-cols-2 gap-2 rounded-lg bg-stone-50 bg-opacity-50 px-3 py-1.5 text-center text-gray-800">
              <div className="text-xs">
                <div className="flex font-bold cursor-pointer">
                  <p className="mr-1 font-normal">New</p>
                  <p>0</p>
                </div>
              </div>
              <div className="text-xs">
                <div className="flex font-bold ">
                  <p className="mr-1 font-normal">unique</p>
                  <p>0</p>
                </div>
              </div>
            </div>
          </div>
        </div>
          {/* <div className="min-w-[14rem] flex-1">
          <div className=" rounded-lg bg-white  pb-2 pt-5 ">
            <div className="flex px-5">
              <h2 className="mb-2 font-semibold text-cm-gray">GGR</h2>
              <button
                className="mantine-UnstyledButton-root smhidden absolute right-0 top-0 rounded-tr-lg p-2 text-cm-gray  hover:bg-stone-200 mantine-auidlw"
                type="button"
              >
                <svg
                  stroke="currentColor"
                  fill="none"
                  strokeWidth={0}
                  viewBox="0 0 24 24"
                  height="1em"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 12C8 13.1046 7.10457 14 6 14C4.89543 14 4 13.1046 4 12C4 10.8954 4.89543 10 6 10C7.10457 10 8 10.8954 8 12Z"
                    fill="currentColor"
                  />
                  <path
                    d="M14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12Z"
                    fill="currentColor"
                  />
                  <path
                    d="M18 14C19.1046 14 20 13.1046 20 12C20 10.8954 19.1046 10 18 10C16.8954 10 16 10.8954 16 12C16 13.1046 16.8954 14 18 14Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
            <div title={0} className="cursor-pointer  flex items-center px-5">
              <p className="text-2xl font-bold">{dashboardData?.totalWithdrawals}</p>
            </div>
            <div className="mx-2 mt-2 grid grid-cols-2 gap-2 rounded-lg bg-stone-50 bg-opacity-50 px-3 py-1.5 text-center text-gray-800">
              <div className="text-xs">
                <div className="flex font-bold cursor-pointer">
                  <p className="mr-1 font-normal">Casino</p>
                  <p>0</p>
                </div>
              </div>
              <div className="text-xs">
                <div className="flex font-bold cursor-pointer">
                  <p className="mr-1 font-normal">SPM Sp</p>
                  <p>0</p>
                </div>
              </div>
            </div>
          </div>
        </div> */}
        <div className="min-w-[14rem] flex-1">
          <div className=" rounded-lg bg-white  pb-2 pt-5 ">
            <div className="flex px-5">
              <h2 className="mb-2 font-semibold text-cm-gray">Deposits</h2>
              
            </div>
            <div title={0} className="cursor-pointer  flex items-center px-5">
              <p className="text-2xl font-bold">{dashboardData?.totalDeposits}</p>
            </div>
            <div className="mx-2 mt-2 grid grid-cols-2 gap-2 rounded-lg bg-stone-50 bg-opacity-50 px-3 py-1.5 text-center text-gray-800">
              <div className="text-xs">
                <div className="flex font-bold cursor-pointer">
                  <p className="mr-1 font-normal">Auto C</p>
                  <p>0</p>
                </div>
              </div>
              <div className="text-xs">
                <div className="flex font-bold cursor-pointer">
                  <p className="mr-1 font-normal">Direct</p>
                  <p>0</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="min-w-[14rem] flex-1">
          <div className=" rounded-lg bg-white  pb-2 pt-5 ">
            <div className="flex px-5">
              <h2 className="mb-2 font-semibold text-cm-gray">Withdraws</h2>
              
            </div>
            <div title={0} className="cursor-pointer  flex items-center px-5">
              <p className="text-2xl font-bold">{dashboardData?.totalWithdrawals}</p>
            </div>
            <div className="mx-2 mt-2 grid grid-cols-2 gap-2 rounded-lg bg-stone-50 bg-opacity-50 px-3 py-1.5 text-center text-gray-800">
              <div className="text-xs">
                <div className="flex font-bold cursor-pointer">
                  <p className="mr-1 font-normal">Auto C</p>
                  <p>0</p>
                </div>
              </div>
              <div className="text-xs">
                <div className="flex font-bold cursor-pointer">
                  <p className="mr-1 font-normal">Direct</p>
                  <p>0</p>
                </div>
              </div>
            </div>
          </div>
        </div>
            
      </div>
     
    </div>


      {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="bg-[#FFFFFF] p-4 rounded-lg ">
          <div className="flex items-center justify-between">
            <Users className="text-black h-6 w-6" />
            <span className="text-black text-xs font-semibold bg-green-400 px-2 py-1 rounded flex gap-1 items-center">
              +12% <ArrowUpCircle size={15} />
            </span>
          </div>
          <p className="text-black text-2xl font-bold mt-2">{userCount}</p>
          <p className="text-black text-sm mt-1">Total Users</p>
          <div className="mt-2 text-black text-xs flex items-center">
            <span>Since last month</span>
            <ArrowRight className="h-4 w-4 ml-1" />
          </div>
        </div>

        <div className="bg-[#FFFFFF] p-4 rounded-lg ">
          <div className="flex items-center justify-between">
            <PiggyBank className="text-black h-6 w-6" />
            <span className="text-black text-xs font-semibold bg-green-400 px-2 py-1 rounded flex gap-1 items-center">
              +12% <ArrowUpCircle size={15} />
            </span>
          </div>
          <p className="text-black text-2xl font-bold mt-2">
            ${totalDeposits?.toFixed(2)}
          </p>
          <p className="text-black text-sm mt-1">Total Deposits</p>
          <div className="mt-2 text-black text-xs flex items-center">
            <span>Since last month</span>
            <ArrowRight className="h-4 w-4 ml-1" />
          </div>
        </div>

        <div className="bg-[#FFFFFF] p-4 rounded-lg ">
          <div className="flex items-center justify-between">
            <Wallet className="text-black h-6 w-6" />
            <span className="text-black text-xs font-semibold bg-red-400 px-2 py-1 rounded flex gap-1 items-center">
              -28% <ArrowDownCircle size={15} />
            </span>
          </div>
          <p className="text-black text-2xl font-bold mt-2">
            ${totalWithdrawals?.toFixed(2)}
          </p>
          <p className="text-black text-sm mt-1">Total Withdrawals</p>
          <div className="mt-2 text-black text-xs flex items-center">
            <span>Since last month</span>
            <ArrowRight className="h-4 w-4 ml-1" />
          </div>
        </div>

        <div className="bg-[#FFFFFF] p-4 rounded-lg ">
          <div className="flex items-center justify-between">
            <UserCheck className="text-black h-6 w-6" />
            <span className="text-black text-xs font-semibold bg-green-400 px-2 py-1 rounded flex gap-1 items-center">
              +84% <ArrowUpCircle size={15} />
            </span>
          </div>
          <p className="text-black text-2xl font-bold mt-2">
            {liveBets.length}
          </p>
          <p className="text-black text-sm mt-1">Live Users</p>
          <div className="mt-2 text-black text-xs flex items-center">
            <span>Since last month</span>
            <ArrowRight className="h-4 w-4 ml-1" />
          </div>
        </div>

        <div className="bg-[#FFFFFF] p-4 rounded-lg ">
          <div className="flex items-center justify-between">
            <ArrowUpCircle className="text-black h-6 w-6" />
            <span className="text-black text-xs font-semibold bg-green-400 px-2 py-1 rounded flex gap-1 items-center">
              +0% <ArrowUpCircle size={15} />
            </span>
          </div>
          <p className="text-black text-2xl font-bold mt-2">
            ${todayDeposits.toFixed(2)}
          </p>
          <p className="text-black text-sm mt-1">Today's Deposit</p>
          <div className="mt-2 text-black text-xs flex items-center">
            <span>Since last month</span>
            <ArrowRight className="h-4 w-4 ml-1" />
          </div>
        </div>

        <div className="bg-[#FFFFFF] p-4 rounded-lg ">
          <div className="flex items-center justify-between">
            <ArrowDownCircle className="text-black h-6 w-6" />
            <span className="text-black text-xs font-semibold bg-green-400 px-2 py-1 rounded flex gap-1 items-center">
              +0% <ArrowUpCircle size={15} />
            </span>
          </div>
          <p className="text-black text-2xl font-bold mt-2">
            ${todayWithdrawals.toFixed(2)}
          </p>
          <p className="text-black text-sm mt-1">Today's Withdrawal</p>
          <div className="mt-2 text-black text-xs flex items-center">
            <span>Since last month</span>
            <ArrowRight className="h-4 w-4 ml-1" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-[#FFFFFF] p-4 rounded-lg shadow-md">
          <h2 className="text-xl text-black font-semibold mb-4">
            Live Bets by Game
          </h2>
          <div className="bg-[#FFFFF] p-6 rounded-lg ">
            <div style={{ height: "400px" }}>
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
        <div className="bg-[#FFFFFF]  p-4 rounded-lg shadow-md">
          <h2 className="text-xl text-black font-semibold mb-4">
            Bet Distribution
          </h2>
          <Pie
            data={pieData}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: "bottom",
                  labels: {
                    color: "black", // This changes the legend text color to white
                  },
                },
              },
              color: "black", // This changes the overall chart text color to white
            }}
          />
        </div>
      </div> */}
    </div>
  );
};

export default AdminDashboard;
